/**
 * Conteo de equipos de la lámina.
 *
 * Dos modos, y el modo cambia qué se cuenta:
 *
 *   - INSTALADO: todos los DEA de terreno están adquiridos. La lámina documenta
 *     lo que hay, así que en altura se cuentan los equipos YA instalados del
 *     cliente y se descuentan los compartidos — un nivel cuyo DEA es el mismo
 *     equipo que uno de terreno no puede contarse dos veces.
 *   - ESTIMACIÓN: hay al menos un DEA propuesto. La lámina dimensiona una
 *     propuesta, así que en altura se cuenta lo que FALTA instalar, y los
 *     compartidos no descuentan nada porque todavía no existen.
 *
 * Los DEA EXTERNOS nunca entran en el conteo del cliente: son de un tercero. Sí
 * afectan el cumplimiento, cubriendo su propio nivel.
 */

import { clientDeaCount, levelsBottomUp, levelsToInstall, missingDeaCount } from './compliance.js';

/** Modo INSTALADO: hay DEA y todos son adquiridos. Sin DEA, la lámina es una estimación. */
export function isInstalledMode(deas) {
  return deas.length > 0 && deas.every((dea) => dea.estado === 'adquirido');
}

/** Equipos del cliente ya instalados en altura, sumando cantidades por nivel. */
export function verticalInstalled(buildings) {
  return buildings.reduce(
    (sum, building) =>
      sum +
      levelsBottomUp(building).reduce((acc, level) => acc + clientDeaCount(building, level), 0),
    0,
  );
}

/** Equipos que falta instalar en altura para cumplir la norma. */
export function verticalMissing(buildings) {
  return buildings.reduce((sum, building) => sum + missingDeaCount(building), 0);
}

/** Niveles declarados como "mismo equipo que un DEA de terreno". */
export function sharedTotal(buildings) {
  return buildings.reduce(
    (sum, building) => sum + Object.keys(building.compartidos || {}).length,
    0,
  );
}

/** Equipos NUEVOS que aporta la altura, sin contar los compartidos ya presentes en terreno. */
export function verticalEquipment(deas, buildings) {
  const installed = isInstalledMode(deas);
  const relevant = installed ? verticalInstalled(buildings) : verticalMissing(buildings);
  const shared = installed ? sharedTotal(buildings) : 0;
  return relevant - shared;
}

/** Total de equipos que representa la lámina. */
export function totalEquipment(deas, buildings) {
  return deas.length + verticalEquipment(deas, buildings);
}

/**
 * Equipos que faltan para cumplir la norma.
 * La brecha horizontal se cuantifica como "al menos 1 más": cuántos DEA hacen
 * falta para llegar al 100% depende de dónde se ubiquen, y eso lo resuelve el
 * operador moviendo círculos, no una fórmula.
 */
export function missingEquipment(coveragePct, buildings) {
  let missing = 0;
  if (coveragePct != null && coveragePct < 100) missing += 1;
  buildings.forEach((building) => {
    missing += levelsToInstall(building).length;
  });
  return missing;
}

/** Total exigido por la norma = instalados + faltantes. */
export function requiredEquipment(deas, buildings, coveragePct) {
  return totalEquipment(deas, buildings) + missingEquipment(coveragePct, buildings);
}
